import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { directusOperations, authManager } from '../../lib/directus';
import { Employee, TelegramUser, AuthState } from '../../lib/types';
import { getWIBTimeForDirectus } from '../../lib/timeUtils';

// Async thunks for API calls
// Note: Telegram authentication is now handled by DirectusAuthProvider
export const authenticateWithTelegram = createAsyncThunk(
  'auth/authenticateWithTelegram',
  async (initData: string, { rejectWithValue }) => {
    // This is deprecated - authentication is now handled by DirectusAuthProvider
    return rejectWithValue('Telegram authentication is deprecated. Use DirectusAuthProvider.');
  }
);

export const fetchEmployeeProfile = createAsyncThunk(
  'auth/fetchEmployeeProfile',
  async (_, { rejectWithValue }) => {
    try {
      // First ensure authentication is valid
      const authState = authManager.getState();
      if (!authState.isAuthenticated || !authState.user) {
        throw new Error('User not authenticated');
      }
      
      return await directusOperations.getCurrentUserProfile();
    } catch (error) {
      console.error('Profile fetch error:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch profile');
    }
  }
);

export const updateEmployeeProfile = createAsyncThunk(
  'auth/updateEmployeeProfile',
  async (data: { daily_target?: number; department_id?: number }, { rejectWithValue }) => {
    try {
      return await directusOperations.updateCurrentUserProfile(data);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update profile');
    }
  }
);

export const fetchEmployeeStats = createAsyncThunk(
  'auth/fetchEmployeeStats',
  async (forceRefresh: boolean = false, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: AuthState };
      const now = Date.now();
      const lastFetched = state.auth.statsLastFetched;
      const cacheTTL = state.auth.statsCacheTTL;
      
      // Check if we have cached data and it's still valid
      if (!forceRefresh && lastFetched) {
        const lastFetchedTime = new Date(lastFetched).getTime();
        if (!isNaN(lastFetchedTime) && (now - lastFetchedTime) < cacheTTL) {
          // Return cached data by rejecting with a special flag
          return rejectWithValue({ cached: true, message: 'Using cached stats data' });
        }
      }
      
      return await directusOperations.getEmployeeStats();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch stats');
    }
  }
);

export const syncTelegramProfile = createAsyncThunk(
  'auth/syncTelegramProfile',
  async (telegramData: TelegramUser, { rejectWithValue }) => {
    // This is deprecated - profile management is now handled by DirectusAuthProvider
    return rejectWithValue('Telegram sync is deprecated. Use DirectusAuthProvider.');
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, _thunkAPI) => {
    try {
      // Use authManager from directus lib to logout
      await authManager.logout();
      
      // Clear all cached data from localStorage
      const keysToRemove = [
        'directus_auth',
        'persist:auth',
        'persist:visits',
        'persist:customers',
        'persist:evidence',
        'persist:root'
      ];
      
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.warn(`Failed to remove ${key} from localStorage:`, e);
        }
      });
      
      // Clear session storage as well
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.clear();
        }
      } catch (e) {
        console.warn('Failed to clear sessionStorage:', e);
      }
      
      return true;
    } catch (error) {
      // Even if server logout fails, we'll clear local state
      console.warn('Server logout failed, clearing local state:', error);
      
      // Clear local storage even if server logout fails
      const keysToRemove = [
        'directus_auth',
        'persist:auth',
        'persist:visits',
        'persist:customers',
        'persist:evidence',
        'persist:root'
      ];
      
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.warn(`Failed to remove ${key} from localStorage:`, e);
        }
      });
      
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.clear();
        }
      } catch (e) {
        console.warn('Failed to clear sessionStorage:', e);
      }
      
      return true;
    }
  }
);

const initialState: AuthState = {
  isAuthenticated: false, // Authentication state is now managed by DirectusAuthProvider
  isLoading: false, // Deprecated - use granular loading states
  profileLoading: false,
  statsLoading: false,
  updateLoading: false,
  employee: null,
  telegramUser: null,
  error: null,
  lastAuthTime: null,
  dailyStats: {
    visitsCompleted: 0,
    dailyTarget: 5,
    weeklyProgress: 0,
    weeklyTarget: 25,
    avgRating: 4.5,
    totalTime: '0h 0m'
  },
  statsLastFetched: null, // For TTL caching
  statsCacheTTL: 5 * 60 * 1000 // 5 minutes in milliseconds
};

// Password login supports the current email field and the legacy Login screen alias.
type LoginCredentials = { password: string } & ({ email: string } | { username: string });

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    const email = 'email' in credentials ? credentials.email : credentials.username;
    const { password } = credentials;
    try {
      await authManager.login(email, password);
      const employee = authManager.getState().user;
      
      if (!employee) {
        return rejectWithValue('Email atau password salah');
      }

      // Check if user is active (database stores as 1/0)
      // if (employee.is_active !== 1) {
      //   return rejectWithValue('Akun tidak aktif. Silahkan hubungi administrator.');
      // }

      // Create user object for app state
      const user = {
        // id: employee.id,
        firstName: employee.first_name,
        lastName: employee.last_name,
        email: employee.email,
        description: employee.description,
        // department: employee.role || 'Employee',
        // dailyTarget: employee.daily_target || 8,
        // employeeId: employee.employee_id,
        // telegramId: employee.telegram_id,
      };

      return { user, employee };
    } catch (error) {
      console.error('Login error:', error);
      return rejectWithValue('Mohon Maaf, Tidak dapat terhubung ke server. Silahkan coba lagi.');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    
    setTelegramUser: (state, action: PayloadAction<TelegramUser>) => {
      state.telegramUser = action.payload;
    },
    
    clearAuthState: (state) => {
      state.isAuthenticated = false;
      state.employee = null;
      state.telegramUser = null;
      state.error = null;
      state.lastAuthTime = null;
    },

    // For development/testing purposes
    setAuthenticatedState: (state, action: PayloadAction<{ employee: Employee; telegramUser?: TelegramUser }>) => {
      state.isAuthenticated = true;
      state.employee = action.payload.employee;
      state.telegramUser = action.payload.telegramUser || null;
      state.lastAuthTime = getWIBTimeForDirectus();
      state.error = null;
      state.isLoading = false;
    },

    // Sync with DirectusAuthProvider
    syncDirectusAuth: (state, action: PayloadAction<{ user: any; isAuthenticated: boolean }>) => {
      state.isAuthenticated = action.payload.isAuthenticated;
      if (action.payload.user && typeof action.payload.user === 'object') {
        // Map Directus user to employee format, ensuring all values are strings/primitives
        state.employee = {
          id: action.payload.user.id || null,
          employee_id: action.payload.user.id || null,
          full_name: `${action.payload.user.first_name || ''} ${action.payload.user.last_name || ''}`.trim(),
          first_name: String(action.payload.user.first_name || ''),
          last_name: String(action.payload.user.last_name || ''),
          email: String(action.payload.user.email || ''),
          department_id: 1, // Default department
          daily_target: 5, // Default target
          is_active: true,
          created_at: getWIBTimeForDirectus(),
          updated_at: getWIBTimeForDirectus(),
        };
        state.lastAuthTime = getWIBTimeForDirectus();
      } else {
        state.employee = null;
        state.lastAuthTime = null;
      }
      state.error = null;
      state.isLoading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Authenticate with Telegram
      .addCase(authenticateWithTelegram.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(authenticateWithTelegram.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.employee = action.payload.profile;
        state.lastAuthTime = getWIBTimeForDirectus();
        state.error = null;
      })
      .addCase(authenticateWithTelegram.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.employee = null;
        state.error = typeof action.payload === 'string' ? action.payload : String(action.payload || 'Authentication failed');
      })
      
      // Fetch employee profile
      .addCase(fetchEmployeeProfile.pending, (state) => {
        state.profileLoading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        // Ensure we have a valid employee object before setting
        if (action.payload && typeof action.payload === 'object') {
          state.employee = action.payload;
        }
        state.error = null;
      })
      .addCase(fetchEmployeeProfile.rejected, (state, action) => {
        state.profileLoading = false;
        // Ensure error is always a string
        state.error = typeof action.payload === 'string' ? action.payload : String(action.payload || 'Unknown error');
      })
      
      // Update employee profile
      .addCase(updateEmployeeProfile.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateEmployeeProfile.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.employee = action.payload;
        state.error = null;
      })
      .addCase(updateEmployeeProfile.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : String(action.payload || 'Profile update failed');
      })
      
      // Fetch employee stats
      .addCase(fetchEmployeeStats.pending, (state) => {
        state.statsLoading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.statsLastFetched = getWIBTimeForDirectus();
        // Update dailyStats with real data from server
        if (action.payload) {
          state.dailyStats = {
            visitsCompleted: action.payload.daily_visits || 0,
            dailyTarget: action.payload.daily_target || 5,
            weeklyProgress: action.payload.daily_visits * 5 || 0, // Estimate weekly from daily
            weeklyTarget: (action.payload.daily_target || 5) * 5, // Weekly = daily * 5
            avgRating: 4.5, // Default until we have rating data
            totalTime: '0h 0m' // Default until we have time tracking
          };
        }
        state.error = null;
      })
      .addCase(fetchEmployeeStats.rejected, (state, action) => {
        state.statsLoading = false;
        // Handle cached data case
        const error = action.payload as any;
        if (error?.cached) {
          // Don't set error for cached data
          console.log('Using cached employee stats');
        } else {
          state.error = typeof error === 'string' ? error : String(error || 'Stats fetch failed');
        }
      })
      
      // Sync Telegram profile
      .addCase(syncTelegramProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(syncTelegramProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.employee = action.payload;
        state.error = null;
      })
      .addCase(syncTelegramProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : String(action.payload || 'Profile sync failed');
      })
      
      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.employee = null;
        state.telegramUser = null;
        state.error = null;
        state.lastAuthTime = null;
        // Reset daily stats on logout
        state.dailyStats = {
          visitsCompleted: 0,
          dailyTarget: 5,
          weeklyProgress: 0,
          weeklyTarget: 25,
          avgRating: 4.5,
          totalTime: '0h 0m'
        };
        state.statsLastFetched = null;
      })
      .addCase(logoutUser.rejected, (state) => {
        // Still clear auth state even if logout API fails
        state.isLoading = false;
        state.isAuthenticated = false;
        state.employee = null;
        state.telegramUser = null;
        state.error = null;
        state.lastAuthTime = null;
        // Reset daily stats on logout
        state.dailyStats = {
          visitsCompleted: 0,
          dailyTarget: 5,
          weeklyProgress: 0,
          weeklyTarget: 25,
          avgRating: 4.5,
          totalTime: '0h 0m'
        };
        state.statsLastFetched = null;
      });
  }
});

export const { 
  clearError, 
  setTelegramUser, 
  clearAuthState,
  setAuthenticatedState,
  syncDirectusAuth
} = authSlice.actions;

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectEmployee = (state: { auth: AuthState }) => state.auth.employee;
export const selectTelegramUser = (state: { auth: AuthState }) => state.auth.telegramUser;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading; // Deprecated
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;

// Granular loading selectors
export const selectProfileLoading = (state: { auth: AuthState }) => state.auth.profileLoading;
export const selectStatsLoading = (state: { auth: AuthState }) => state.auth.statsLoading;
export const selectUpdateLoading = (state: { auth: AuthState }) => state.auth.updateLoading;
export const selectDailyStats = (state: { auth: AuthState }) => state.auth.dailyStats;

// Combined loading selector for Dashboard
export const selectCriticalDataLoading = (state: { auth: AuthState }) => 
  state.auth.profileLoading;

// Cache status selector
export const selectStatsNeedRefresh = (state: { auth: AuthState }) => {
  const { statsLastFetched, statsCacheTTL } = state.auth;
  if (!statsLastFetched) return true;
  const now = Date.now();
  const lastFetched = new Date(statsLastFetched).getTime();
  // Handle invalid dates
  if (isNaN(lastFetched)) return true;
  return (now - lastFetched) >= statsCacheTTL;
};

export default authSlice.reducer;